/**
 * Loading state components
 * - Skeleton: Placeholder for content loading
 * - Spinner: For action loading states
 * - Progress: For upload/download progress
 */

import { useEffect, useState } from 'react';

// Skeleton loader for content placeholders
interface SkeletonProps {
  className?: string;
  /**
   * Announce loading state to screen readers
   */
  ariaLabel?: string;
}

export function Skeleton({ className = '', ariaLabel }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-gallery-700 rounded ${className}`}
      aria-hidden={!ariaLabel}
      aria-label={ariaLabel}
      role={ariaLabel ? 'status' : undefined}
    />
  );
}

// Card skeleton for gallery/artwork items
export function CardSkeleton() {
  return (
    <div className="bg-gallery-900 rounded-xl overflow-hidden">
      <Skeleton className="aspect-[4/3] w-full rounded-none" />
      <div className="p-4">
        <Skeleton className="h-6 w-3/4 mb-2" />
        <Skeleton className="h-4 w-full mb-1" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

// List item skeleton
export function ListItemSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 bg-gallery-900 rounded-lg">
      <Skeleton className="w-12 h-12 rounded" />
      <div className="flex-1">
        <Skeleton className="h-5 w-1/3 mb-2" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

// Stat card skeleton
export function StatSkeleton() {
  return (
    <div className="p-6 bg-gallery-900 rounded-xl">
      <Skeleton className="h-4 w-24 mb-2" />
      <Skeleton className="h-8 w-32" />
    </div>
  );
}

// Spinner for action loading states
interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Announce loading state to screen readers
   */
  ariaLabel?: string;
}

export function Spinner({ size = 'md', className = '', ariaLabel = 'Loading' }: SpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-2',
    lg: 'w-12 h-12 border-3',
  };

  return (
    <div
      className={`animate-spin rounded-full border-accent-500 border-t-transparent ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label={ariaLabel}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
}

// Full page loader
export function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Loading page">
      <Spinner size="lg" />
      <span className="sr-only">Loading page...</span>
    </div>
  );
}

// Button loading state
interface ButtonSpinnerProps {
  className?: string;
}

export function ButtonSpinner({ className = '' }: ButtonSpinnerProps) {
  return (
    <Spinner size="sm" className={className} ariaLabel="Processing" />
  );
}

// Progress bar for uploads
interface ProgressProps {
  value: number; // 0-100
  max?: number;
  className?: string;
  /**
   * Show percentage text
   */
  showLabel?: boolean;
  /**
   * Announce progress updates to screen readers
   */
  announceInterval?: number; // Announce every N percent
}

export function Progress({
  value,
  max = 100,
  className = '',
  showLabel = false,
  announceInterval = 25,
}: ProgressProps) {
  const [announced, setAnnounced] = useState(0);
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  // Announce progress updates at intervals for screen readers
  useEffect(() => {
    const nextAnnounce = Math.floor(percentage / announceInterval) * announceInterval;
    if (nextAnnounce > announced && nextAnnounce <= 100) {
      setAnnounced(nextAnnounce);
    }
  }, [percentage, announceInterval, announced]);

  return (
    <div className={`w-full ${className}`}>
      <div
        className="h-2 bg-gallery-800 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(percentage)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${Math.round(percentage)}% complete`}
      >
        <div
          className="h-full bg-accent-500 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <p className="text-sm text-gallery-400 mt-1 text-center">
          {Math.round(percentage)}%
        </p>
      )}
      {/* Screen reader announcement */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announced > 0 && `${announced}% complete`}
      </div>
    </div>
  );
}

// Upload progress with file info
interface UploadProgressProps {
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
  onCancel?: () => void;
  onRetry?: () => void;
}

export function UploadProgress({
  fileName,
  progress,
  status,
  error,
  onCancel,
  onRetry,
}: UploadProgressProps) {
  return (
    <div className="p-4 bg-gallery-900 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium truncate max-w-[200px]">{fileName}</span>
        <span className="text-xs text-gallery-500">
          {status === 'uploading' && `${Math.round(progress)}%`}
          {status === 'processing' && 'Processing...'}
          {status === 'complete' && 'Complete'}
          {status === 'error' && 'Failed'}
        </span>
      </div>

      {status === 'uploading' && (
        <Progress value={progress} />
      )}

      {status === 'processing' && (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <span className="text-sm text-gallery-400">Processing file...</span>
        </div>
      )}

      {status === 'complete' && (
        <div className="flex items-center gap-2 text-green-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm">Upload complete</span>
        </div>
      )}

      {status === 'error' && (
        <div className="text-red-400">
          <p className="text-sm mb-2">{error || 'Upload failed'}</p>
          <div className="flex gap-2">
            {onRetry && (
              <button
                onClick={onRetry}
                className="text-xs px-3 py-1 bg-gallery-800 hover:bg-gallery-700 rounded transition-colors"
              >
                Retry
              </button>
            )}
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-xs px-3 py-1 text-gallery-500 hover:text-white transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}

      {status === 'uploading' && onCancel && (
        <button
          onClick={onCancel}
          className="mt-2 text-xs text-gallery-500 hover:text-white transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
